import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Link,
  useTheme,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { Visibility, VisibilityOff, Fingerprint } from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import { Navigate, Link as RouterLink } from "react-router-dom";
import { authClient } from "../lib/auth-client";
import { useNavigate } from "react-router-dom";
import { tenant } from "../lib/branding";


const LOGIN_GRACE_KEY = "auth_login_grace_until";
const LOGIN_GRACE_DURATION_MS = 15000;

function markRecentLogin() {
  if (typeof window === "undefined") return;
  window.sessionStorage.setItem(
    LOGIN_GRACE_KEY,
    String(Date.now() + LOGIN_GRACE_DURATION_MS),
  );
}

async function getSessionWithRetries(attempts = 3, delayMs = 400) {
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    const result = await authClient.getSession({
      fetchOptions: { force: true },
    });

    if (result?.data) {
      return result.data;
    }

    if (attempt < attempts - 1) {
      await new Promise((resolve) => window.setTimeout(resolve, delayMs));
    }
  }

  return null;
}

const PASSKEY_SETUP_HINT =
  "Para empezar a usar esta función activala en configuración.";

function getPasskeyErrorMessage(error) {
  const status = error?.status;
  const rawMessage =
    error?.message || error?.error?.message || error?.cause?.message || "";
  const normalizedMessage = String(rawMessage).toLowerCase();
  const cancelledByUser =
    error?.name === "NotAllowedError" ||
    normalizedMessage.includes("auth cancelled") ||
    normalizedMessage.includes("authentication cancelled") ||
    normalizedMessage.includes("operation canceled") ||
    normalizedMessage.includes("operation cancelled") ||
    normalizedMessage.includes("the operation either timed out or was not allowed");

  if (cancelledByUser) {
    return "Cancelaste la verificación con huella o Face ID.";
  }

  const missingPasskey =
    status === 404 ||
    normalizedMessage.includes("passkey") ||
    normalizedMessage.includes("credential") ||
    normalizedMessage.includes("not found") ||
    normalizedMessage.includes("not registered") ||
    normalizedMessage.includes("no passkeys") ||
    normalizedMessage.includes("not configured");

  if (missingPasskey) {
    return PASSKEY_SETUP_HINT;
  }

  return rawMessage || "Error al iniciar sesión con passkey";
}

const LoginForm = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { data: session, isPending: sessionPending } = authClient.useSession();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [formErrors, setFormErrors] = useState({ email: "", password: "" });

  const [showResend, setShowResend] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);

  const handleResendVerification = async () => {
    if (!email) {
      setErrorMsg("Por favor, ingresá tu email para reenviar el correo de verificación.");
      return;
    }
    setResendLoading(true);
    setErrorMsg("");
    try {
      const response = await authClient.sendVerificationEmail({
        email,
        callbackURL: `${window.location.origin}/verify-email`,
      });
      if (response?.error) {
        setErrorMsg(response.error.message || "Error al reenviar el correo de verificación.");
      } else {
        setErrorMsg("¡Enlace de verificación enviado! Por favor, revisá tu correo.");
        setShowResend(false);
      }
    } catch (err) {
      console.error(err);
      setErrorMsg("Error de conexión al intentar reenviar el correo.");
    } finally {
      setResendLoading(false);
    }
  };

  const [openForgot, setOpenForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotStatus, setForgotStatus] = useState({ type: "", msg: "" });
  const [forgotLoading, setForgotLoading] = useState(false);
  const pendingToken = typeof window === "undefined"
    ? null
    : window.localStorage.getItem("pendingInvitationToken");
  const revokedAccessMessage = session && !session.barbershopId && !session.isOrganizationOwner && !pendingToken
    ? "Tu acceso fue sido revocado o no tenés una barbería activa."
    : "";

  useEffect(() => {
    if (session && !session.barbershopId && !session.isOrganizationOwner) {
      if (pendingToken) {
        // If there's a pending invitation, redirect them back to the invitation page
        navigate(`/accept-invitation/${pendingToken}`);
      }
    }
  }, [session, navigate, pendingToken]);

  if (sessionPending) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100dvh",
          bgcolor: "background.default",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (session) {
    if (session.barbershopId || session.isOrganizationOwner) {
      const target = session.isActive === false ? "/sales" : "/";
      return <Navigate to={target} replace />;
    }
  }

  const handleClickShowPassword = () => setShowPassword(!showPassword);

  const validateForm = () => {
    let valid = true;
    let errors = { email: "", password: "" };

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      errors.email = "El correo es obligatorio";
      valid = false;
    } else if (!emailRegex.test(email)) {
      errors.email = "Formato de correo inválido";
      valid = false;
    }

    if (!password) {
      errors.password = "La contraseña es obligatoria";
      valid = false;
    } else if (password.length < 6) {
      errors.password = "Debe tener al menos 6 caracteres";
      valid = false;
    }

    setFormErrors(errors);
    return valid;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    setFormErrors({ email: "", password: "" });

    if (!validateForm()) return;

    setLoading(true);

    try {
      const response = await authClient.signIn.email({
        email,
        password,
      });

      if (response.error) {
        const status = response.error.status;
        const message = response.error.message;
        const code = response.error.code;

        const isUnverified =
          code === "EMAIL_NOT_VERIFIED" ||
          message?.toLowerCase().includes("verify") ||
          message?.toLowerCase().includes("verified");

        if (isUnverified) {
          setShowResend(true);
          setErrorMsg("Tu correo no está verificado. Por favor, verificalo para continuar.");
        } else {
          switch (status) {
            case 400:
              setErrorMsg("Solicitud incorrecta. Verificá los datos ingresados.");
              break;
            case 401:
              setErrorMsg("Correo o contraseña incorrectos.");
              break;
            case 403:
              setErrorMsg("No tenés permiso para acceder a este recurso.");
              break;
            case 404:
              setErrorMsg("El servicio de autenticación no fue encontrado.");
              break;
            case 429:
              setErrorMsg("Demasiados intentos. Por favor, espera un momento.");
              break;
            case 500:
              setErrorMsg("Error interno del servidor. Inténtalo más tarde.");
              break;
            default:
              setErrorMsg(message || "Ocurrió un error inesperado");
          }
        }
        setLoading(false);
      } else {
        markRecentLogin();
        // Fetch full session to get custom fields (isActive, role, etc.)
        const fullSession = await getSessionWithRetries();
        setLoading(false);
        const target = fullSession?.isActive === false ? "/sales" : "/";
        navigate(target);
      }
    } catch {
      setErrorMsg("No se pudo conectar con el servidor. Revisa tu conexión.");
      setLoading(false);
    }
  };

  const handlePasskeySignIn = async () => {
    setErrorMsg("");
    setLoading(true);
    try {
      const { passkeyAuthClient } = await import("../lib/passkey-auth-client");
      const response = await passkeyAuthClient.signIn.passkey();
      if (response.error) {
        setErrorMsg(getPasskeyErrorMessage(response.error));
      } else {
        markRecentLogin();
        // Fetch full session to get custom fields
        const fullSession = await getSessionWithRetries();
        const target = fullSession?.isActive === false ? "/sales" : "/";
        navigate(target);
      }
    } catch (error) {
      setErrorMsg(getPasskeyErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    const email = forgotEmail.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!email) {
      setForgotStatus({ type: "error", msg: "El correo es obligatorio" });
      return;
    }

    if (!emailRegex.test(email)) {
      setForgotStatus({ type: "error", msg: "El formato del correo es inválido" });
      return;
    }

    setForgotLoading(true);
    try {
      const response = await authClient.requestPasswordReset({
        email,
        redirectTo: "/reset-password",
      });

      if (response.error) {
        let msg = "No se pudo procesar la solicitud";
        if (response.error.status === 404) msg = "Si el correo está registrado, recibirás un enlace pronto.";
        if (response.error.status === 429) msg = "Demasiados intentos. Esperá unos minutos.";

        setForgotStatus({
          type: response.error.status === 404 ? "success" : "error",
          msg: response.error.message || msg,
        });
      } else {
        setForgotStatus({
          type: "success",
          msg: "Se ha enviado un enlace a tu correo para restablecer la contraseña.",
        });
      }
    } catch {
      setForgotStatus({
        type: "error",
        msg: "Error de conexión al intentar recuperar la contraseña",
      });
    } finally {
      setForgotLoading(false);
    }
  };

  const closeForgotDialog = () => {
    setOpenForgot(false);
    setForgotEmail("");
    setForgotStatus({ type: "", msg: "" });
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        bgcolor: "background.default",
        color: "text.primary",
      }}
    >
      {/* LEFT PANEL: Branding & Visuals (Visible only on desktop/tablet) */}
      <Box
        sx={{
          flex: "1 1 50%",
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          justifyContent: "space-between",
          p: 6,
          background: theme.palette.mode === "light"
            ? `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`
            : `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`,
          position: "relative",
          overflow: "hidden",
          color: "#ffffff",
        }}
      >
        {/* Background Glowing Blobs */}
        <Box
          sx={{
            position: "absolute",
            top: -100,
            left: -100,
            width: 400,
            height: 400,
            borderRadius: "50%",
            background: theme.palette.mode === "light"
              ? `radial-gradient(circle, ${alpha(theme.palette.primary.light, 0.15)} 0%, transparent 70%)`
              : `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.12)} 0%, transparent 70%)`,
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: -50,
            right: -50,
            width: 350,
            height: 350,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${alpha(theme.palette.secondary.main, 0.08)} 0%, transparent 70%)`,
            pointerEvents: "none",
          }}
        />

        {/* Logo/Brand Name */}
        <Box sx={{ display: "flex", alignItems: "center", zIndex: 2 }}>
          {tenant.isRivas ? (
            <img
              src={tenant.logo(theme.palette.mode)}
              alt="Logo"
              style={{ maxHeight: '60px' }}
            />
          ) : (
            <Typography
              variant="h5"
              sx={{
                fontStyle: 'italic',
                fontWeight: 700,
                color: theme.palette.mode === 'light' ? '#10B981' : 'primary.main',
                letterSpacing: '0.02em',
                lineHeight: 1,
                fontSize: '2rem',
              }}
            >
              {tenant.name}
            </Typography>
          )}
        </Box>

        {/* Core Slogan/Glass Card */}
        <Box
          sx={{
            zIndex: 2,
            maxWidth: 480,
            my: "auto",
            p: 4,
            borderRadius: 4,
            border: "1px solid rgba(255, 255, 255, 0.08)",
            background: "linear-gradient(135deg, rgba(255, 255, 255, 0.04) 0%, rgba(255, 255, 255, 0.01) 100%)",
            backdropFilter: "blur(20px)",
            boxShadow: "0 20px 50px rgba(0,0,0,0.15)",
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              mb: 2,
              lineHeight: 1.2,
              letterSpacing: "-0.03em",
              background: "linear-gradient(180deg, #ffffff 0%, #cbd5e1 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            El control de tu salón en tus manos.
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "rgba(255, 255, 255, 0.7)",
              lineHeight: 1.6,
              fontSize: "1.05rem",
              fontWeight: 400,
            }}
          >
            Gestioná turnos, colaboradores, comisiones y liquidaciones con la herramienta líder diseñada para potenciar tu marca.
          </Typography>
        </Box>

        {/* Footer info */}
        <Box sx={{ zIndex: 2 }}>
          <Typography variant="caption" sx={{ color: "rgba(255, 255, 255, 0.4)" }}>
            © {new Date().getFullYear()} Blade
          </Typography>
        </Box>
      </Box>

      {/* RIGHT PANEL: Form (Takes full width on mobile, 50% on desktop) */}
      <Box
        sx={{
          flex: { xs: "1 1 100%", md: "1 1 50%" },
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          p: { xs: 3, sm: 6 },
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Subtle right-side glowing blob for PC version */}
        <Box
          sx={{
            display: { xs: "none", md: "block" },
            position: "absolute",
            top: "15%",
            right: "10%",
            width: 300,
            height: 300,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.05 : 0.04)} 0%, transparent 70%)`,
            pointerEvents: "none",
            zIndex: 0,
          }}
        />

        <Box sx={{ width: "100%", maxWidth: 360, zIndex: 1, position: "relative" }}>
          {/* Subtle mobile logo in flow */}
          <Box
            sx={{
              display: { xs: "flex", md: "none" },
              alignItems: "center",
              justifyContent: "center",
              mb: tenant.isRivas ? 1.5 : 2.5,
              mt: tenant.isRivas ? -3 : 0,
            }}
          >
            {tenant.isRivas ? (
              <img
                src={tenant.logo(theme.palette.mode)}
                alt="Logo"
                style={{ maxHeight: '64px', objectFit: 'contain' }}
              />
            ) : (
              <Typography
                variant="h5"
                sx={{
                  fontStyle: 'italic',
                  fontWeight: 800,
                  color: 'primary.main',
                  letterSpacing: '-0.02em',
                  lineHeight: 1,
                  fontSize: '1.8rem',
                }}
              >
                {tenant.name}
              </Typography>
            )}
          </Box>

          <Typography
            variant="h4"
            sx={{ mb: 1, fontWeight: 800, letterSpacing: "-0.03em", textAlign: { xs: "center", md: "left" } }}
          >
            Iniciar sesión
          </Typography>
          <Typography
            variant="body2"
            sx={{ mb: 4, color: "text.secondary", fontWeight: 400, textAlign: { xs: "center", md: "left" } }}
          >
            Ingresá tus credenciales para acceder a tu cuenta
          </Typography>

          {(errorMsg || revokedAccessMessage) && (
            <Alert
              severity={errorMsg.includes("Enlace de verificación enviado") ? "success" : "error"}
              sx={{ mb: 3, borderRadius: 3 }}
              action={
                showResend && (
                  <Button
                    color="inherit"
                    size="small"
                    onClick={handleResendVerification}
                    disabled={resendLoading}
                    sx={{ fontWeight: 700, textTransform: 'none' }}
                  >
                    {resendLoading ? <CircularProgress size={16} color="inherit" /> : 'Reenviar'}
                  </Button>
                )
              }
            >
              {errorMsg || revokedAccessMessage}
            </Alert>
          )}

          <Box component="form" noValidate onSubmit={handleLogin}>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontWeight: 600,
                mb: 0.75,
                display: "block",
              }}
            >
              Email
            </Typography>
            <TextField
              fullWidth
              placeholder="tu@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              error={!!formErrors.email}
              helperText={formErrors.email}
              sx={{ mb: 3 }}
            />

            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontWeight: 600,
                mb: 0.75,
                display: "block",
              }}
            >
              Contraseña
            </Typography>
            <TextField
              fullWidth
              placeholder="••••••"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              error={!!formErrors.password}
              helperText={formErrors.password}
              sx={{ mb: 1.5 }}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleClickShowPassword}
                        edge="end"
                        sx={{ color: "text.secondary" }}
                        aria-label={
                          showPassword
                            ? "Ocultar contraseña"
                            : "Mostrar contraseña"
                        }
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }
              }}
            />

            <Box
              sx={{ display: "flex", justifyContent: "flex-end", mb: 3.5 }}
            >
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={(e) => {
                  e.preventDefault();
                  setOpenForgot(true);
                }}
                underline="hover"
                sx={{
                  fontSize: "0.8rem",
                  color: "primary.main",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                ¿Olvidaste tu contraseña?
              </Link>
            </Box>

            <Button
              fullWidth
              type="submit"
              variant="contained"
              disabled={loading}
              sx={{
                py: 1.75,
                bgcolor: "primary.main",
                color: "primary.contrastText",
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.9rem",
                borderRadius: "8px",
                "&:hover": { bgcolor: "primary.dark" },
                "&.Mui-disabled": { bgcolor: "action.disabledBackground", color: "text.disabled" },
                mb: 3,
              }}
            >
              {loading ? (
                <CircularProgress size={24} sx={{ color: "inherit" }} />
              ) : (
                "Iniciar sesión"
              )}
            </Button>

            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <Divider sx={{ flexGrow: 1 }} />
              <Typography
                variant="caption"
                sx={{ px: 2, color: "text.secondary", fontWeight: 600, letterSpacing: "0.06em" }}
              >
                O BIEN
              </Typography>
              <Divider sx={{ flexGrow: 1 }} />
            </Box>

            <Button
              fullWidth
              variant="outlined"
              onClick={handlePasskeySignIn}
              disabled={loading}
              startIcon={<Fingerprint />}
              sx={{
                py: 1.75,
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.85rem",
                borderRadius: "8px",
                borderColor: "divider",
                color: "text.primary",
                "&:hover": {
                  bgcolor: "action.hover",
                  borderColor: "text.primary",
                },
              }}
            >
              Iniciar sesión con huella o FaceID
            </Button>

            <Typography
              variant="caption"
              sx={{
                mt: 2,
                display: "block",
                textAlign: "center",
                color: "text.secondary",
                lineHeight: 1.5,
                fontWeight: 400,
              }}
            >
              {PASSKEY_SETUP_HINT}
            </Typography>

            <Box sx={{ mt: 3, textAlign: "center" }}>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  lineHeight: 1.6,
                  fontSize: "0.85rem",
                }}
              >
                ¿No tenés una cuenta?{" "}
                <Link
                  component={RouterLink}
                  to="/onboarding"
                  underline="hover"
                  sx={{ fontWeight: 700, color: "text.primary" }}
                >
                  Elegí tu plan
                </Link>
                , registrate y comenzá a usar la aplicación hoy mismo.
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Forgot Password Dialog */}
      <Dialog
        open={openForgot}
        onClose={closeForgotDialog}
        maxWidth="xs"
        fullWidth
        slotProps={{
          paper: {
            sx: { borderRadius: 3, p: 1 }
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, letterSpacing: "-0.02em" }}>Recuperar contraseña</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2, color: 'text.secondary' }}>
            Ingresá tu correo electrónico y te vamos a mandar un enlace para
            restablecer tu contraseña.
          </DialogContentText>
          {forgotStatus.msg && (
            <Alert severity={forgotStatus.type} sx={{ mb: 2, borderRadius: 2 }}>
              {forgotStatus.msg}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Correo electrónico"
            type="email"
            fullWidth
            variant="outlined"
            value={forgotEmail}
            onChange={(e) => setForgotEmail(e.target.value)}
            disabled={forgotLoading || forgotStatus.type === "success"}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button
            onClick={closeForgotDialog}
            sx={{ borderRadius: 2, textTransform: 'none', color: 'text.secondary' }}
            disabled={forgotLoading}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleForgotPassword}
            variant="contained"
            disabled={forgotLoading || forgotStatus.type === "success"}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            {forgotLoading ? <CircularProgress size={24} /> : "Enviar enlace"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LoginForm;
